// Port-uniqueness rule for connectors.
//
// Within the set of connectors connecting blocks A and B (in either
// direction), each (blockId, port) pair may appear at most once.
// Goal: dual-directional flows are allowed, but a single port on a
// single block can't anchor multiple lines toward the same partner.
//
//   ✓ A.top → B.top  +  A.top-right → B.top-left
//   ✗ A.top → B.top  +  A.bottom    → B.top      (B.top reused)
//   ✗ A.top → B.top  +  A.top       → B.bottom   (A.top reused)
//
// The rule is partner-scoped: A.top may still connect to a *different*
// block C even while it's used in an A↔B link. Enforcement is at
// UI-creation time only — legacy/imported workflows are tolerated.

import type { Connector, CrossSheetConnector, PortSide } from '@/types/workflow';

const DEFAULT_FROM: PortSide = 'right';
const DEFAULT_TO: PortSide = 'left';

/**
 * Connectors that share the same (unordered) endpoint pair as the
 * candidate. Returned for callers that want to inspect existing port
 * usage before creating.
 */
function connectorsBetweenBlocks(
  connectors: readonly Connector[], aId: string, bId: string,
): Connector[] {
  return connectors.filter(c =>
    (c.fromId === aId && c.toId === bId) ||
    (c.fromId === bId && c.toId === aId)
  );
}

function crossSheetConnectorsBetween(
  connectors: readonly CrossSheetConnector[],
  fromSheetIndex: number, toSheetIndex: number,
  fromBlockId: string, toBlockId: string,
): CrossSheetConnector[] {
  return connectors.filter(c => {
    const forward =
      c.fromSheetIndex === fromSheetIndex && c.toSheetIndex === toSheetIndex &&
      c.fromBlockId === fromBlockId && c.toBlockId === toBlockId;
    const reverse =
      c.fromSheetIndex === toSheetIndex && c.toSheetIndex === fromSheetIndex &&
      c.fromBlockId === toBlockId && c.toBlockId === fromBlockId;
    return forward || reverse;
  });
}

/**
 * Check whether (fromId, fromPort) and (toId, toPort) can both join an
 * existing same-sheet link between these two blocks without reusing a
 * port. Pass an `excludeConnectorId` to ignore one connector — used by
 * createConnector when the candidate already exists (toggle case)
 * since the caller intends to remove it, not re-add.
 */
export function isPortPairAvailable(
  connectors: readonly Connector[],
  fromId: string, toId: string,
  fromPort: PortSide = DEFAULT_FROM,
  toPort: PortSide = DEFAULT_TO,
  excludeConnectorId?: string,
): boolean {
  const peers = connectorsBetweenBlocks(connectors, fromId, toId)
    .filter(c => c.id !== excludeConnectorId);
  for (const c of peers) {
    const usedFp = c.fromPort ?? DEFAULT_FROM;
    const usedTp = c.toPort ?? DEFAULT_TO;
    // (block, port) pairs already used on this relation.
    if (c.fromId === fromId && usedFp === fromPort) return false;
    if (c.toId === fromId && usedTp === fromPort) return false;
    if (c.fromId === toId && usedFp === toPort) return false;
    if (c.toId === toId && usedTp === toPort) return false;
  }
  return true;
}

/**
 * Cross-sheet variant. The (sheetIndex, blockId) tuple identifies a
 * unique block, so uniqueness applies per (sheetIndex, blockId, port).
 */
export function isCrossSheetPortPairAvailable(
  connectors: readonly CrossSheetConnector[],
  fromSheetIndex: number, toSheetIndex: number,
  fromBlockId: string, toBlockId: string,
  fromPort: PortSide = DEFAULT_FROM,
  toPort: PortSide = DEFAULT_TO,
): boolean {
  const peers = crossSheetConnectorsBetween(
    connectors, fromSheetIndex, toSheetIndex, fromBlockId, toBlockId,
  );
  for (const c of peers) {
    const usedFp = c.fromPort ?? DEFAULT_FROM;
    const usedTp = c.toPort ?? DEFAULT_TO;
    const usedFromKey = `${c.fromSheetIndex}:${c.fromBlockId}:${usedFp}`;
    const usedToKey = `${c.toSheetIndex}:${c.toBlockId}:${usedTp}`;
    const newFromKey = `${fromSheetIndex}:${fromBlockId}:${fromPort}`;
    const newToKey = `${toSheetIndex}:${toBlockId}:${toPort}`;
    if (usedFromKey === newFromKey || usedToKey === newFromKey) return false;
    if (usedFromKey === newToKey || usedToKey === newToKey) return false;
  }
  return true;
}

/**
 * The set of (block, port) keys already in use across all same-sheet
 * connectors between two blocks. Used by `getReturnPortPair` to skip
 * occupied port combinations when picking a parallel return path.
 */
export function getUsedPortKeys(
  connectors: readonly Connector[], aId: string, bId: string,
): Set<string> {
  const used = new Set<string>();
  for (const c of connectorsBetweenBlocks(connectors, aId, bId)) {
    used.add(`${c.fromId}:${c.fromPort ?? DEFAULT_FROM}`);
    used.add(`${c.toId}:${c.toPort ?? DEFAULT_TO}`);
  }
  return used;
}
